import * as React from 'react';

import { WeightDisplayUnit } from '@rematter/frontend-lib-api-client';

import { WeightUnitType } from '~/modules/trade/types/load';

export const toWeightUnitType = (
  displayUnit: WeightDisplayUnit | null | undefined,
  defaultUnit: WeightDisplayUnit = 'PriceUnit',
): WeightUnitType => {
  const unit = displayUnit ?? defaultUnit;
  return unit === 'PriceUnit' ? 'price' : 'scale';
};

type UseWeightUnitManagerParams = {
  weightDisplayUnitPreference?: WeightDisplayUnit | null;
  onWeightDisplayUnitChange?: (unit: WeightDisplayUnit) => void;
  defaultUnit?: WeightDisplayUnit;
};

type UseWeightUnitManagerReturn = {
  currentWeightUnit: WeightUnitType;
  handleWeightUnitChange: (unit: WeightUnitType) => void;
  effectiveWeightDisplayUnit: WeightDisplayUnit;
};

export const useWeightUnitManager = ({
  weightDisplayUnitPreference,
  onWeightDisplayUnitChange,
  defaultUnit = 'PriceUnit',
}: UseWeightUnitManagerParams): UseWeightUnitManagerReturn => {
  const toWeightUnitTypeWithDefault = React.useCallback(
    (displayUnit: WeightDisplayUnit | null | undefined): WeightUnitType =>
      toWeightUnitType(displayUnit, defaultUnit),
    [defaultUnit],
  );

  const toWeightDisplayUnit = React.useCallback(
    (unitType: WeightUnitType): WeightDisplayUnit =>
      unitType === 'price' ? 'PriceUnit' : 'ScaleUnit',
    [],
  );

  const effectiveWeightDisplayUnit = React.useMemo(
    (): WeightDisplayUnit => weightDisplayUnitPreference ?? defaultUnit,
    [weightDisplayUnitPreference, defaultUnit],
  );

  const currentWeightUnit = React.useMemo(
    () => toWeightUnitTypeWithDefault(effectiveWeightDisplayUnit),
    [effectiveWeightDisplayUnit, toWeightUnitTypeWithDefault],
  );

  const handleWeightUnitChange = React.useCallback(
    (unit: WeightUnitType) => {
      const mappedUnit = toWeightDisplayUnit(unit);
      onWeightDisplayUnitChange?.(mappedUnit);
    },
    [onWeightDisplayUnitChange, toWeightDisplayUnit],
  );

  return {
    currentWeightUnit,
    handleWeightUnitChange,
    effectiveWeightDisplayUnit,
  };
};
